import { useEffect, useState } from "react";
import { doFetch } from "@/util/fetch";
import { getIdToken } from "@/util/authentication";
import { apiServer } from "@/util/apiServer";
import { ManualStripeIntentResponse } from "@/api/manualPaymentService";

export function useManualStripePaymentIntent(requestUuid: string, token?: string, paymentMethod?: string) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!requestUuid) return;

    let cancelled = false;

    const fetchIntent = async () => {
      setLoading(true);
      setError(null);

      try {
        const headers: Record<string, string> = { Accept: "application/json" };
        const query = new URLSearchParams();

        if (token) {
          query.set("token", token);
        } else {
          const idToken = await getIdToken();
          if (!idToken) {
            setError("Session missing. Please logout and login again.");
            setLoading(false);
            return;
          }

          headers.Authorization = `Bearer ${idToken.replace(/\"/g, "")}`;
        }

        const response = await doFetch(
          `${apiServer ?? ""}/api/orders/payment/manual/${encodeURIComponent(requestUuid)}/stripe/create${query.toString() ? `?${query.toString()}` : ""}`,
          {
            method: "POST",
            headers: {
              ...headers,
              "Content-Type": "application/json",
            },
            body: paymentMethod ? JSON.stringify({ paymentMethod }) : undefined,
          }
        );

        if (!response.ok) {
          const content = await (async () => {
            try {
              return await response.json();
            } catch {
              return await response.text();
            }
          })();
          throw new Error(typeof content === "object" ? content.message || JSON.stringify(content) : String(content));
        }

        const data = (await response.json()) as ManualStripeIntentResponse;
        if (cancelled) return;

        setClientSecret(data.clientSecret);
        setAmount(data.amount);
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Could not load secure payment.";
        console.error("useManualStripePaymentIntent error:", err);
        setError(message);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchIntent();

    return () => {
      cancelled = true;
    };
  }, [requestUuid, token, paymentMethod]);

  return { clientSecret, amount, error, loading };
}
